import {useContext} from 'react';
import useAutocomplete from '@material-ui/lab/useAutocomplete';
import NoSsr from '@material-ui/core/NoSsr';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import styled from 'styled-components';
import {UserContext} from "../../core/context/user";

const InputWrapper = styled('div')`
  width: 100%;
  border: 1px solid transparent;
  background-color: '#383838';
  border-radius: 3px;
  padding: 1px;
  display: flex;
  flex-wrap: wrap;

  &:hover {
    border-color: #FE6502;
  }

  &.focused {
    border-color: #FE6502;
  }

  & input {
    font-size: 1rem;
    height: 30px;
    box-sizing: border-box;
    background-color: '#383838';
    color: #B2B2B2;
    padding: 4px 6px;
    width: 0;
    min-width: 30px;
    flex-grow: 1;
    border: 0;
    margin: 0;
    outline: 0;
  }
`;

const Tag = styled(({ label, onDelete, ...props }) => (
    <div {...props}>
        <span>{label}</span>
        <CloseIcon onClick={onDelete} />
    </div>
))`
  display: flex;
  align-items: center;
  height: 24px;
  margin: 2px;
  line-height: 22px;
  background-color: #707070;
  color: #EFEFEF;
  border-radius: 2px;
  box-sizing: content-box;
  padding: 0 4px 0 10px;
  outline: 0;
  overflow: hidden;

  &:focus {
    border-color: #40a9ff;
    background-color: #e6f7ff;
  }

  & span {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  & svg {
    font-size: 12px;
    cursor: pointer;
    padding: 4px;
  }
`;

const Listbox = styled('ul')`
  width: 300px;
  margin: 2px 0 0;
  padding: 0;
  position: absolute;
  list-style: none;
  background-color: ${window.localStorage.getItem('theme') !== 'light' ? '#383838' : '#EFEFEF'};
  overflow: auto;
  max-height: 250px;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  z-index: 1;
  color: #B2B2B2;

  & li {
    padding: 5px 12px;
    display: flex;

    & span {
      flex-grow: 1;
    }

    & svg {
      color: transparent;
    }
  }

  & li[aria-selected='true'] {
    background-color: #fafafa;
    font-weight: 600;
    color: black;
    
    & svg {
      color: #1890ff;
    }
  }

  & li[data-focus='true'] {
    background-color: #e6f7ff;
    cursor: pointer;
    color: black;

    & svg {
      color: #000;
    }
  }
`;

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export default function TypeaheadChipMultiselect({options, selectedOptions, onChange} : any): any {
    const {
        getRootProps,
        getInputProps,
        getTagProps,
        getListboxProps,
        getOptionProps,
        groupedOptions,
        value,
        focused,
        setAnchorEl,
    } = useAutocomplete({
        id: 'customized-hook-demo',
        defaultValue: selectedOptions !== null ? selectedOptions : [],
        multiple: true,
        options: options || [],
        getOptionLabel: (option) => option.name,
        onChange: (_event, value) => onChange(value)
    });

    const userContext = useContext(UserContext);

    return (
        <NoSsr>
            <div>
                <div {...getRootProps()}>
                    <InputWrapper
                        ref={setAnchorEl}
                        className={focused ? 'focused' : ''}
                        style={userContext.lightTheme ? {backgroundColor: '#EFEFEF'} : {backgroundColor: '#383838'}}
                    >
                        {value.map((option, index) => (
                            <Tag label={option.name} {...getTagProps({ index })} />
                        ))}
                        <input {...getInputProps()}
                               style={
                                   userContext.lightTheme
                                       ? {backgroundColor: '#EFEFEF'}
                                       : {backgroundColor: '#383838'}}
                        />
                    </InputWrapper>
                </div>
                {groupedOptions.length > 0 ? (
                    <Listbox {...getListboxProps()}
                             style={
                                 userContext.lightTheme
                                     ? {backgroundColor: '#EFEFEF'}
                                     : {backgroundColor: '#383838'}
                             }>
                        {groupedOptions.map((option, index) => (
                            <li {...getOptionProps({ option, index })}>
                                <span>{option.name}</span>
                                <CheckIcon fontSize="small" />
                            </li>
                        ))}
                    </Listbox>
                ) : null}
            </div>
        </NoSsr>
    );
}
